// store/searchBot.ts
import { SearchBotQuestionAnswerResult } from '../contracts/api/QuerySearchBot/QuerySearchBot.Response'
import { escapeHtml } from '../composition/escapeHtml'

// Используем enum для строгости
export enum MessageTypes {
  QUEST = 'quest',
  ANSWER_MONO = 'answer_mono',
  ANSWER_MULTI = 'answer_multi',
  ANSWER_RATE = 'answer_rate',
  ANSWER_ERROR = 'answer_error',
  ANSWER_RATE_RESPONSE = 'answer_rate_response',
  NOT_FOUND = 'not_found',
}

export enum ListTypes {
  RAND = 'RAND',
  ROUND = 'ROUND',
}

export enum SearchBotRate {
  UP = 5,
  DOWN = 1,
}

// Типы диалога и сообщений
type DialogQuestion = {
  id: string
  name: string
  result?: SearchBotQuestionAnswerResult
}

type DialogLink = {
  id: string
  url: string
  title: string
}

type DialogItem = {
  id: string
  type: 'answer' | 'question'
  title: string
  questions?: DialogQuestion[]
  links?: DialogLink[]
  result?: SearchBotQuestionAnswerResult
}

export type Message = {
  id: number
  reqid: string
  showRate: boolean
  type: MessageTypes
  text: string
  title?: string
  answers?: Array<{ id: string; text: string }>
}

export type DialogContext = {
  location_: string
  device_: string
  // eslint-disable-next-line camelcase
  device_os_: string
}

export type DialogInfo = {
  duid: string
  context: DialogContext
}

export type nanoMessage = {
  type: string
  text: string | null
}

export type State = {
  dialogList: DialogItem[]
  isShow: boolean
  notFoundList: string[]
  notFoundText: string
  counter: number
  messages: Message[]
  nextId: number

  dialogUID: string
  nanoGreetMessages: nanoMessage[]
  nanoGreetBtn: nanoMessage[]
  dialog: DialogInfo
}

export const state = (): State => ({
  dialogList: [],
  isShow: false,
  notFoundList: [],
  notFoundText: '',
  counter: -1,
  messages: [],
  nextId: 0,

  dialogUID: '',
  nanoGreetMessages: [],
  nanoGreetBtn: [],
  dialog: null,
})

export const getters = {
  isShown: (state: State) => state.isShow,
  dialogList: (state: State) => state.dialogList,
  notFoundList: (state: State) => state.notFoundList,
  notFoundText: (state: State) => state.notFoundText,
  counter: (state: State) => state.counter,
  messages: (state: State) => state.messages,
  getDialogUID: (state: State) => state.dialogUID,
  getDialog: (state: State) => state.dialog,
  getNanoGreetMessages: (state: State) => state.nanoGreetMessages,
  getNanoGreetBtn: (state: State) => state.nanoGreetBtn,
}

export const mutations = {
  SET_DIALOG_UID: (state: State, value: string) => {
    state.dialogUID = value
  },

  SET_DIALOG: (state: State, value: DialogInfo) => {
    state.dialog = value
  },

  SET_NANO_GREET_BTN(state: State, value: nanoMessage) {
    state.nanoGreetBtn.push(value)
  },

  SET_NANO_GREET_MESSAGES(state: State, value: nanoMessage) {
    state.nanoGreetMessages.push(value)
  },

  PUSH_MESSAGE(state: State, payload: Omit<Message, 'id'>) {
    const message: Message = {
      id: state.nextId++,
      ...payload,
    }
    console.log('PUSH_MESSAGE', message)
    state.messages.unshift(message)
  },

  CLEAR_MESSAGES(state: State) {
    state.messages = []
  },

  DELETE_MESSAGE(state: State, payload: number) {
    const idx = state.messages.findIndex((m) => m.id === payload)
    if (idx > -1) {
      state.messages.splice(idx, 1)
    }
  },

  SET_IS_SHOWN(state: State, value: boolean) {
    state.isShow = value
  },

  ADD_DIALOG_ITEM(state: State, item: DialogItem) {
    state.dialogList.push(item)
    localStorage.setItem('chatHistory', JSON.stringify(state.dialogList))
  },

  LOAD_DIALOG_HISTORY(state: State) {
    const saved = localStorage.getItem('chatHistory')
    if (saved) state.dialogList = JSON.parse(saved)
  },

  CLEAR_DIALOG_HISTORY(state: State) {
    state.messages = []
  },

  SET_NOT_FOUND_LIST(
    state: State,
    { typeList, list }: { typeList: ListTypes; list: string[] }
  ) {
    state.notFoundList = state.notFoundList.length ? state.notFoundList : list
    state.notFoundText =
      typeList === ListTypes.RAND
        ? state.notFoundList[
            Math.floor(Math.random() * state.notFoundList.length)
          ]
        : ((state.counter = (state.counter + 1) % state.notFoundList.length),
          state.notFoundList[state.counter])
  },
}

export const actions = {
  sendQuestion({ commit }: any, questionText: string) {
    const text = escapeHtml(questionText)
    commit('PUSH_MESSAGE', { type: MessageTypes.QUEST, text })
  },

  async nextQuestion({ commit }: any, { questionId, questionName }: any) {
    commit('PUSH_MESSAGE', { type: MessageTypes.QUEST, text: questionName })
    await handleSingleResult.call(this, questionId, commit)
  },
}

// Вынесенная логика для обработки одиночного результата
async function handleSingleResult(this: any, id: string, commit: any) {
  const result = await this.$api.requests.getSearchBotNextQuestion(id)
  if (result.status === 500) {
    return commit('PUSH_MESSAGE', { type: MessageTypes.NOT_FOUND, text: '' })
  }

  const next = result.answers.nextQuestion
  if (!next.length) {
    commit('PUSH_MESSAGE', {
      type: MessageTypes.ANSWER_MONO,
      text: result.answers.result,
    })
  } else {
    const nextQs = await Promise.all(
      next.map((qid: string) =>
        this.$api.requests.getSearchBotNextQuestion(+qid)
      )
    )
    commit('PUSH_MESSAGE', {
      type: MessageTypes.ANSWER_MULTI,
      text: '',
      answers: nextQs.map((r: any) => ({ id: r.id, text: r.title })),
    })
  }
}

export const namespaced = true
